import { mergeMap, Subscription } from 'rxjs';
// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationError,
  Router,
} from '@angular/router';
import {
  environment,
  InactivityService,
  LayoutConfigService,
  RegisterService,
  RouterService,
  SearchPopupComponent,
  SeoService,
  SessionService,
  SplashScreenService,
  STABLE_LANGUAGE,
  SUPPORTED_LANGUAGES,
  User,
  UsetifulService,
} from '@cai-common';
import {
  IOffice,
  IOfficeAccess,
  UserAdsEnum,
  UserService,
} from '@cai-services';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { filter, map } from 'rxjs/operators';
import Swal from 'sweetalert2';

const LOCAL_HOST = '//localhost',
  AUTH_URL = '/auth',
  REGISTER_URL = '/register',
  ERROR = '/error';

@Component({
  selector: 'div[kt-root]',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  // Public properties
  title = 'Metronic';
  loader: boolean;
  hasNewVersion = false;
  modalRef: NgbModalRef;
  isFirstRef: boolean;
  private officeChangedStatusSub: Subscription;
  sessionService: SessionService;
  registerService: RegisterService;
  private fetchInterval: ReturnType<typeof setInterval> | null = null;
  private seoService: SeoService;
  splashScreenService: SplashScreenService;
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  /**
   * Component constructor
   *
   * @param router: Router
   * @param layoutConfigService: LayoutCongifService
   * @param splashScreenService: SplashScreenService
   */
  constructor(
    private router: Router,
    private layoutConfigService: LayoutConfigService,
    private readonly usetifulService: UsetifulService,
    private readonly modalService: NgbModal,
    private inactivityService: InactivityService,
    private readonly cdr: ChangeDetectorRef,
    private readonly routerSvc: RouterService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly injector: Injector,
    private readonly userService: UserService,
  ) {
    this.registerService = injector.get<RegisterService>(RegisterService);
    this.sessionService = injector.get<SessionService>(SessionService);
    this.seoService = injector.get<SeoService>(SeoService);
    this.splashScreenService =
      injector.get<SplashScreenService>(SplashScreenService);
  }

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit(): void {
    this.routerSvc.checkStorage();
    // enable/disable loader
    this.loader = this.layoutConfigService.getConfig('loader.enabled');
    const routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (this.inMainApp(event.url)) {
          this.translateApp(event?.url);
          this.usetifulService.initUsetiful();
        }
        // hide splash screen
        this.splashScreenService.hide();
        this.closeActiveModals();

        // scroll to top on every route change
        window.scrollTo(0, 0);

        // to display back the body content
        setTimeout(() => {
          document.body.classList.add('kt-page--loaded');
        }, 0);
      }
    });

    this.router.events
      .pipe(
        filter((evt) => evt instanceof NavigationError),
        map((evt) => evt as NavigationError),
      )
      .subscribe((evt) => {
        if (evt.error instanceof Error && evt.error.name == 'ChunkLoadError') {
          window.location.assign(evt.url);
        }
      });

    this.unsubscribe.push(routerSubscription);

    this.checkEnvironmentVersion();

    this.initializeSeo();

    this.isFirstRef = true;

    this.officeChangedStatusSub =
      this.sessionService.officeChangedStatusObs.subscribe((_) => {
        if (!this.isFirstRef) {
          setTimeout(() => {
            this.showTrialAds();
          }, 500);
        }
      });

    this.isFirstRef = false;

    setTimeout(() => {
      this.showTrialAds();
    }, 1000);
  }

  private showTrialAds(): void {
    const currentUser = this.sessionService.getCurrentUser(),
      selectedOffice = this.sessionService.getSelectedOffice();

    if (currentUser && selectedOffice) {
      const currentOfficeAccess = this.getCurrentOfficeAccess(
        currentUser,
        selectedOffice,
      );

      if (currentOfficeAccess) {
        this.displayAdBasedOnOfficeAccess(currentOfficeAccess);
      }
    }
  }

  private getCurrentOfficeAccess(
    currentUser: User,
    selectedOffice: IOffice,
  ): IOfficeAccess {
    return currentUser?.officeAccesses?.find(
      (officeAccess) => officeAccess?.office?.id === selectedOffice?.id,
    );
  }

  async displayAdBasedOnOfficeAccess(
    currentOfficeAccess: IOfficeAccess,
  ): Promise<void> {
    if (
      currentOfficeAccess?.office?.shouldDisplayAd === UserAdsEnum.TRIAL_START
    ) {
      this.openSearchPopup(UserAdsEnum.TRIAL_START);
      await this.userService.deleteUserOfficeAd(
        currentOfficeAccess?.office?.id?.toString(),
        UserAdsEnum.TRIAL_START,
      );
      await this.updateUserData();
    } else if (
      currentOfficeAccess?.office?.shouldDisplayAd === UserAdsEnum.TRIAL_END
    ) {
      this.openSearchPopup(UserAdsEnum.TRIAL_END);
      await this.userService.deleteUserOfficeAd(
        currentOfficeAccess?.office?.id?.toString(),
        UserAdsEnum.TRIAL_END,
      );
      await this.updateUserData();
    } else {
      this.closeModal();
    }
  }

  async updateUserData(): Promise<void> {
    try {
      const user = await this.registerService.getUser();
      this.sessionService.setCurrentUser(user);
    } catch (error) {
      console.error('Failed to update user data:', error);
    }
  }

  private openSearchPopup(campaignId: UserAdsEnum): void {
    this.modalService?.dismissAll();

    this.modalRef = this.modalService.open(SearchPopupComponent, {
      windowClass: 'search-popup',
      keyboard: false,
      backdrop: 'static',
      centered: true,
    });

    const resultsComponent = this.modalRef
      .componentInstance as SearchPopupComponent;
    if (resultsComponent) {
      resultsComponent.shouldShowSearchPopup = true;
      resultsComponent.isPromotion = true;
      resultsComponent.title =
        campaignId === UserAdsEnum.TRIAL_START
          ? $localize`:@@app.free-trial-activated:Free Trial Activated`
          : $localize`:@@app.free-trial-expired:Free Trial Expired`;
      resultsComponent.campaignId = campaignId;
    }
  }

  private closeModal(): void {
    if (this.modalRef?.close) {
      this.modalRef.close();
    }
  }

  private inMainApp(url: string) {
    return (
      url !== '/' &&
      !url.includes(AUTH_URL) &&
      !url.includes(REGISTER_URL) &&
      !url.includes(ERROR)
    );
  }

  async translateApp(url: string) {
    const ALLOWED_LANGUAGES = !environment?.features?.newLanguages
        ? STABLE_LANGUAGE
        : SUPPORTED_LANGUAGES,
      checkUrlLanguage = ALLOWED_LANGUAGES.filter(
        (language) => window.location.href?.indexOf(`/${language}/`) >= 0,
      ),
      isLocalHost = window.location.href.indexOf(LOCAL_HOST) >= 0;
    if (checkUrlLanguage?.length || isLocalHost) {
      return;
    } else {
      const token = this.activatedRoute.snapshot.paramMap.get('token'),
        storedUser = this.sessionService.getCurrentUser(),
        user = storedUser
          ? storedUser
          : await this.registerService.getUser(token);
      if (user?.language === 'en' || !user?.language) {
        return;
      }
      const new_route = `/${user?.language}${url}`;
      window.location.replace(new_route);
    }
  }

  /**
   * On Destroy
   */
  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
    if (this.officeChangedStatusSub) {
      this.officeChangedStatusSub.unsubscribe();
    }
    if (this.modalRef?.close) {
      this.modalRef.close();
    }
    if (this.fetchInterval) {
      clearInterval(this.fetchInterval);
      this.fetchInterval = null;
    }
  }

  checkEnvironmentVersion() {
    this.fetchInterval = setInterval(
      () => {
        fetch('assets/environment/app-config.json')
          .then(async (response) => {
            const env = await response.json();
            this.hasNewVersion = env.version !== environment.version;
            this.cdr.detectChanges();
          })
          .catch((err) => {
            console.warn('unable to fetch app config', err);
          });
      },
      1000 * 60 * 10,
    );
  }

  onReloadClick() {
    window.location.reload();
  }

  // copied from https://stackoverflow.com/questions/48330535/dynamically-add-meta-description-based-on-route-in-angular
  private initializeSeo(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
      )
      .subscribe((event) => {
        this.seoService.updateDescription(event.description);
      });
  }

  private closeActiveModals() {
    if (Swal.isVisible()) {
      Swal.close();
    }
    if (this.modalService.hasOpenModals()) {
      this.modalService.dismissAll();
    }
  }
}
